import { type CustomerAgeingData } from "@/types/ageing-analysis";
import { type Transaction } from "@/types/transaction";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Search, Settings, Briefcase, HandCoins, AlertTriangle, FileText, Download, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import { useState, useMemo, useCallback } from "react";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences";
import { UniqueTableNamesForColumnConfiguration } from "@/config/unique-table-names-for-column-configuration";
import { toast } from "sonner";
import { TransactionDetailSheet } from "@/components/transactions/transaction-detail-sheet";
import { CreatePromiseDrawer } from "@/components/collections/create-promise-drawer";
import { CreateCaseDrawer } from "@/components/collections/create-case-drawer";
import { AddNoteModal, CreateDisputeModal, type NoteFormData } from "@/components/transactions/transaction-action-modals";
import { SendEmailModal } from "@/components/collections/action-modals";
import { SelectionToolbar } from "@/components/transactions/selection-toolbar";
import { useCustomerTransactionsPaginatedGraphQL } from "@/hooks/useTransactionsGraphQL";
import { useCompany } from "@/contexts/CompanyContext";
import { DataTable, type DataTableColumn } from "@/components/shared/data-table";

interface TransactionsTabProps {
  customer: CustomerAgeingData;
  customerId?: string;
  formatCurrency: (amount: number, currency?: string) => string;
}

const DEFAULT_OUTSTANDING_TRANSACTIONS_COLUMNS: ColumnDefinition[] = [
  { id: 'referenceNumber', label: 'Reference #', visible: true, order: 0 },
  { id: 'description', label: 'Description', visible: true, order: 1 },
  { id: 'flags', label: 'Flags', visible: true, order: 2 },
  { id: 'date', label: 'Date', visible: true, order: 3 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 4 },
  { id: 'amount', label: 'Gross', visible: true, order: 5 },
  { id: 'outstanding', label: 'Outstanding', visible: true, order: 6 },
  { id: 'daysOverdue', label: 'Days Overdue', visible: true, order: 7 },
];

export function TransactionsTab({ customer, customerId, formatCurrency }: Readonly<TransactionsTabProps>) {
  const { selectedCompany } = useCompany();
  const baseCurrency = selectedCompany?.currency ?? "GBP";

  // Format invoice amounts in the customer's own currency by default
  const formatInvoiceAmount = useCallback(
    (amount: number) => formatCurrency(amount, customer.currency),
    [formatCurrency, customer.currency]
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [isColumnConfigOpen, setIsColumnConfigOpen] = useState(false);
  
  // Transaction detail modal state
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  
  // Create Collection Case modal state
  const [isCreateCaseModalOpen, setIsCreateCaseModalOpen] = useState(false);
  
  // Add Note modal state
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isCreateDisputeModalOpen, setIsCreateDisputeModalOpen] = useState(false);
  const [isSendCopyInvoiceModalOpen, setIsSendCopyInvoiceModalOpen] = useState(false);
  const [isRecordPromiseModalOpen, setIsRecordPromiseModalOpen] = useState(false);
  
  // Column preferences management
  const {
    columns: outstandingColumns,
    visibleColumns: visibleOutstandingColumns,
    savePreferences: saveOutstandingPreferences,
    resetToDefaults: resetOutstandingPreferences,
  } = useScreenColumnPreferences(
    UniqueTableNamesForColumnConfiguration.AgeingAnalysisCustomerOutstandingTransactions,
    DEFAULT_OUTSTANDING_TRANSACTIONS_COLUMNS
  );

  // Sorting state
  type SortField = "referenceNumber" | "date" | "dueDate" | "amount" | "outstanding" | "daysOverdue" | "ageing";
  type SortDirection = "asc" | "desc" | null;
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Default column widths
  const defaultColumnWidths: Record<string, number> = {
    referenceNumber: 130,
    description: 180,
    flags: 90,
    date: 110,
    dueDate: 110,
    amount: 120,
    outstanding: 120,
    daysOverdue: 110,
  };

  // Sortable field set
  const sortableFields = new Set(["referenceNumber", "date", "dueDate", "amount", "outstanding", "daysOverdue"]);

  // Selection state
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<Set<string>>(new Set());


  // Selection handlers
  const selectAll = () => {
    setSelectedTransactionIds(new Set(filteredInvoices.map(inv => inv.id)));
  };

  const deselectAll = () => {
    setSelectedTransactionIds(new Set());
  };

  const selectTransaction = useCallback((transactionId: string) => {
    setSelectedTransactionIds(prev => {
      const next = new Set(prev);
      next.add(transactionId);
      return next;
    });
  }, []);

  const deselectTransaction = useCallback((transactionId: string) => {
    setSelectedTransactionIds(prev => {
      const next = new Set(prev);
      next.delete(transactionId);
      return next;
    });
  }, []);

  // Map-based handlers to satisfy SonarQube S2301 (no boolean branching)
  const selectAllActions = useMemo(() => ({
    true: selectAll,
    false: deselectAll,
  }), [selectAll, deselectAll]);

  const handleSelectAllToggle = useCallback(
    (checked: boolean) => selectAllActions[String(checked) as 'true' | 'false'](),
    [selectAllActions],
  );

  const handleSelectRowToggle = useCallback(
    (id: string | number, checked: boolean) => {
      const actions = { true: selectTransaction, false: deselectTransaction };
      actions[String(checked) as 'true' | 'false'](id as string);
    },
    [selectTransaction, deselectTransaction],
  );

  const handleClearSelection = () => {
    setSelectedTransactionIds(new Set());
  };

  // Action handlers
  const handleExportSelected = () => {
    toast.success(`Exporting ${selectedTransactionIds.size} selected transactions...`);
  };

  const handleSendCopyInvoice = () => {
    setIsSendCopyInvoiceModalOpen(true);
  };

  const handleCreateCollectionCase = () => {
    setIsCreateCaseModalOpen(true);
  };

  const handleCreatePromise = () => {
    toast.info("Use the Create Promise drawer from Case Details to record a promise.");
  };

  const handleSavePromise = () => {
    toast.success("Promise to pay recorded successfully");
    setIsRecordPromiseModalOpen(false);
  };

  const handleCaseCreated = () => {
    console.log("Collection case created successfully");
    setIsCreateCaseModalOpen(false);
  };

  const handleCreateDispute = () => {
    setIsCreateDisputeModalOpen(true);
  };

  const handleAddNote = () => {
    setIsAddNoteModalOpen(true);
  };

  const handleSaveNote = (noteData: NoteFormData) => {
    console.log("Saving note:", noteData);
    toast.success("Note added successfully");
    setIsAddNoteModalOpen(false);
    // Clear selection after adding note
    setSelectedTransactionIds(new Set());
  };

  // Convert OutstandingInvoice to Transaction for the modal
  const convertInvoiceToTransaction = (invoice: typeof outstandingInvoices[0]): Transaction => {
    return {
      id: parseInt(invoice.id) || 0,
      referenceNumber: invoice.invoiceNumber,
      transactionDate: invoice.date,
      customerId: 1,
      customerName: customer.customerName,
      transactionType: invoice.transactionType ?? "invoice",
      amount: invoice.amount,
      currency: customer.currency,
      status: invoice.daysOverdue > 0 ? "overdue" : "outstanding",
      description: invoice.description || `Invoice ${invoice.invoiceNumber}`,
      createdBy: "System",
      createdAt: invoice.date,
      dueDate: invoice.dueDate,
      outstandingAmount: invoice.outstanding,
      onDispute: invoice.flags?.isDisputed,
    };
  };

  // Handle opening transaction detail modal
  const handleOpenTransactionDetail = (invoice: typeof outstandingInvoices[0]) => {
    const transaction = convertInvoiceToTransaction(invoice);
    setSelectedTransaction(transaction);
    setIsTransactionModalOpen(true);
  };


  // Handle column sorting - cycles asc -> desc -> clear
  const handleSort = (field: string) => {
    const typedField = field as SortField;
    if (sortField === typedField) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortDirection(null);
        setSortField(null);
      }
    } else {
      setSortField(typedField);
      setSortDirection("asc");
    }
  };


  const getOverdueColor = (daysOverdue: number) => {
    if (daysOverdue <= 0) return "text-green-600";
    if (daysOverdue <= 30) return "text-green-600";
    if (daysOverdue <= 60) return "text-yellow-600";
    if (daysOverdue <= 90) return "text-orange-600";
    if (daysOverdue <= 120) return "text-red-600";
    return "text-red-700";
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "dd/MM/yyyy");
    } catch {
      return dateString;
    }
  };

  // Map UI sort field names to sortFieldMap keys used by useCustomerTransactionsPaginatedGraphQL
  const sortFieldToGraphQL: Record<string, string> = {
    referenceNumber: "referenceNumber",
    date: "transactionDate",
    dueDate: "dueDate",
    amount: "amount",
    outstanding: "outstandingAmount",
    daysOverdue: "daysOverdue",
  };

  const graphQLSortBy = sortField && sortDirection ? sortFieldToGraphQL[sortField] ?? "transactionDate" : "transactionDate";
  const graphQLSortOrder = sortDirection ?? "desc";

  const { transactions: liveTransactions, pagination: livePagination, isLoading } = useCustomerTransactionsPaginatedGraphQL(
    customerId,
    {
      page: currentPage,
      pageSize,
      sortBy: graphQLSortBy,
      sortOrder: graphQLSortOrder,
      outstandingStatus: "outstanding",
    },
  );

  // Map Transaction objects to OutstandingInvoice format for the existing table UI
  const outstandingInvoices = useMemo(() => {
    return liveTransactions.map((tx) => ({
      id: String(tx.id),
      invoiceNumber: tx.referenceNumber,
      date: tx.transactionDate,
      dueDate: tx.dueDate ?? tx.transactionDate,
      amount: tx.amount,
      outstanding: tx.outstandingAmount ?? 0,
      daysOverdue: tx.daysOverdue ?? 0,
      ageingBucket: tx.ageingBucket ?? "current",
      description: tx.description,
      transactionType: tx.transactionType,
      flags: {
        isDisputed: tx.onDispute ?? tx.hasDispute,
        hasPromiseToPay: tx.hasPromise,
        isInCollectionCase: tx.hasCase,
      },
    }));
  }, [liveTransactions]);

  // Filter invoices based on search query and ageing bucket filter (client-side on current page)
  const filteredInvoices = useMemo(() => {
    return outstandingInvoices.filter((invoice) => {
      const query = searchQuery.toLowerCase();
      const matchesSearch = !query || (
        invoice.invoiceNumber.toLowerCase().includes(query) ||
        invoice.amount.toString().includes(query) ||
        invoice.outstanding.toString().includes(query) ||
        formatDate(invoice.date).toLowerCase().includes(query) ||
        formatDate(invoice.dueDate).toLowerCase().includes(query)
      );
      return matchesSearch;
    });
  }, [outstandingInvoices, searchQuery]);

  const totalRecords = livePagination?.totalItems ?? 0;
  const totalPages = livePagination?.totalPages || Math.ceil(totalRecords / pageSize);

  // Convert visible columns to DataTableColumn format
  const dataTableColumns: DataTableColumn[] = useMemo(() => {
    return visibleOutstandingColumns.map((col) => ({
      id: col.id,
      label: col.label,
      sortKey: sortableFields.has(col.id) ? col.id : undefined,
      align: (col.id === "amount" || col.id === "outstanding") ? "right" as const : undefined,
      defaultWidth: defaultColumnWidths[col.id] ?? 120,
    }));
  }, [visibleOutstandingColumns]);

  // Render cell content for each column
  const renderCell = useCallback((columnId: string, invoice: typeof outstandingInvoices[0]) => {
    switch (columnId) {
      case "referenceNumber":
        return (
          <span className="text-left font-medium">
            {invoice.invoiceNumber}
          </span>
        );
      case "description":
        return <span>{invoice.description || "-"}</span>;
      case "flags":
        return (
          <TooltipProvider>
            <div className="flex items-center gap-2">
              {invoice.flags?.isDisputed && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 transition-colors cursor-help">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-red-600 text-white border-red-700">
                    <p className="font-semibold">Disputed</p>
                    <p className="text-xs">This transaction is under dispute</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {invoice.flags?.hasPromiseToPay && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors cursor-help">
                      <HandCoins className="h-4 w-4 text-blue-600" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-blue-600 text-white border-blue-700">
                    <p className="font-semibold">Promise to Pay</p>
                    <p className="text-xs">Customer has committed to payment</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {invoice.flags?.isInCollectionCase && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 hover:bg-orange-200 transition-colors cursor-help">
                      <Briefcase className="h-4 w-4 text-orange-600" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-orange-600 text-white border-orange-700">
                    <p className="font-semibold">Collection Case</p>
                    <p className="text-xs">Part of an active collection case</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {!invoice.flags?.isDisputed && !invoice.flags?.hasPromiseToPay && !invoice.flags?.isInCollectionCase && (
                <span className="text-muted-foreground text-xs">-</span>
              )}
            </div>
          </TooltipProvider>
        );
      case "date":
        return <span>{formatDate(invoice.date)}</span>;
      case "dueDate":
        return <span>{formatDate(invoice.dueDate)}</span>;
      case "amount":
        return <span className="font-medium">{formatInvoiceAmount(invoice.amount)}</span>;
      case "outstanding":
        return <span className="font-semibold">{formatInvoiceAmount(invoice.outstanding)}</span>;
      case "daysOverdue":
        return (
          <span className={cn("font-semibold", getOverdueColor(invoice.daysOverdue))}>
            {invoice.daysOverdue > 0 ? `${invoice.daysOverdue} days` : "Current"}
          </span>
        );
      default:
        return null;
    }
  }, [formatInvoiceAmount, formatDate, getOverdueColor, handleOpenTransactionDetail]);

  // Calculate selected transactions totals
  const selectedTransactions = useMemo(() => {
    return filteredInvoices.filter(inv => selectedTransactionIds.has(inv.id));
  }, [filteredInvoices, selectedTransactionIds]);

  const selectedTotals = useMemo(() => {
    const totalAmount = selectedTransactions.reduce((sum, inv) => sum + inv.amount, 0);
    const totalOutstanding = selectedTransactions.reduce((sum, inv) => sum + inv.outstanding, 0);
    return {
      totalAmount,
      totalOutstanding,
      currency: customer.currency,
    };
  }, [selectedTransactions, customer.currency]);

  // Convert selected invoices to Transaction[] for SelectionToolbar
  const selectedTransactionsAsTransaction = useMemo(() => {
    return selectedTransactions.map(convertInvoiceToTransaction);
  }, [selectedTransactions]);

  // Check if all filtered invoices are selected
  const allSelected = filteredInvoices.length > 0 && selectedTransactionIds.size === filteredInvoices.length;

  // Export transactions to CSV
  const handleExportCsv = useCallback(() => {
    if (filteredInvoices.length === 0) {
      toast.info("No transactions to export");
      return;
    }
    const columns = visibleOutstandingColumns;
    const header = columns.map(col => `"${col.label}"`).join(",");
    const rows = filteredInvoices.map(invoice =>
      columns.map(col => {
        let value = "";
        switch (col.id) {
          case "referenceNumber": value = invoice.invoiceNumber; break;
          case "description": value = invoice.description || ""; break;
          case "flags": {
            const flags: string[] = [];
            if (invoice.flags?.isDisputed) flags.push("Disputed");
            if (invoice.flags?.hasPromiseToPay) flags.push("Promise to Pay");
            if (invoice.flags?.isInCollectionCase) flags.push("Collection Case");
            value = flags.join(", ");
            break;
          }
          case "date": value = formatDate(invoice.date); break;
          case "dueDate": value = formatDate(invoice.dueDate); break;
          case "amount": value = String(invoice.amount); break;
          case "outstanding": value = String(invoice.outstanding); break;
          case "daysOverdue": value = invoice.daysOverdue > 0 ? String(invoice.daysOverdue) : "0"; break;
          default: break;
        }
        return `"${value.replace(/"/g, '""')}"`;
      }).join(",")
    );
    const csvContent = [header, ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${customer.customerName.replace(/[^a-zA-Z0-9]/g, "_")}_transactions_${format(new Date(), "yyyyMMdd")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${filteredInvoices.length} transactions to CSV`);
  }, [filteredInvoices, visibleOutstandingColumns, formatDate, customer.customerName]);

  return (
    <div className="relative h-full">
      <ScrollArea className="h-full">
        <div className="space-y-6">
          {/* Transactions Table */}
          <Card className="flex flex-col min-h-0">
          <CardHeader className="pb-2 flex-none">
            <div className="flex items-center justify-between gap-4">
              <CardTitle className="text-base whitespace-nowrap">
                Outstanding Transactions ({totalRecords})
              </CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative w-56">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search transactions..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    className="pl-8 h-8 text-sm"
                  />
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        onClick={handleExportCsv}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top"><p>Export to CSV</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        onClick={() => setIsColumnConfigOpen(true)}
                      >
                        <Settings className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top"><p>Configure Columns</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0 flex flex-col min-h-0 flex-1 p-0">
            <DataTable
              columns={dataTableColumns}
              data={filteredInvoices}
              getRowId={(invoice) => invoice.id}
              renderCell={renderCell}
              sortField={sortField ?? undefined}
              sortDirection={sortDirection ?? undefined}
              onSort={handleSort}
              selectedIds={selectedTransactionIds}
              allSelected={allSelected}
              onSelectAll={handleSelectAllToggle}
              onSelectRow={handleSelectRowToggle}
              isLoading={isLoading}
              loadingContent={
                <div className="flex flex-col items-center justify-center space-y-4 py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Loading transactions...</p>
                </div>
              }
              emptyContent={
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="h-16 w-16 mx-auto mb-4 opacity-20" />
                  {outstandingInvoices.length === 0 ? (
                    <>
                      <p className="text-sm font-medium">No Outstanding Transactions</p>
                      <p className="text-xs mt-1">This customer has no outstanding invoices</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium">No Matching Transactions</p>
                      <p className="text-xs mt-1">Try adjusting your search query</p>
                    </>
                  )}
                </div>
              }
              pagination={{
                currentPage,
                totalPages: totalPages || 1,
                pageSize,
                totalItems: totalRecords,
                onPageChange: setCurrentPage,
                onPageSizeChange: (size) => { setPageSize(size); setCurrentPage(1); },
                pageSizeOptions: [5, 10, 25, 50],
              }}
            />
          </CardContent>
        </Card>

        </div>
      </ScrollArea>

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={isColumnConfigOpen}
        onOpenChange={setIsColumnConfigOpen}
        columns={outstandingColumns}
        onApply={saveOutstandingPreferences}
        onReset={resetOutstandingPreferences}
      />

      {/* Transaction Detail Sheet */}
      <TransactionDetailSheet
        open={isTransactionModalOpen}
        onOpenChange={setIsTransactionModalOpen}
        transaction={selectedTransaction}
      />

      {/* Create Promise Drawer */}
      <CreatePromiseDrawer
        open={isRecordPromiseModalOpen}
        onOpenChange={setIsRecordPromiseModalOpen}
        onSave={handleSavePromise}
        prePopulatedCustomer={{
          customerId: customer.id,
          customerName: customer.customerName,
          accountNumber: customer.accountNumber,
        }}
        prePopulatedTransactions={
          selectedTransactions.length > 0
            ? selectedTransactions.map((t) => ({
                id: t.id,
                headerReference: t.invoiceNumber,
                invoiceDate: new Date(t.date),
                originalAmount: t.amount,
                balanceRemaining: t.outstanding,
                daysOverdue: t.daysOverdue,
              }))
            : undefined
        }
      />

      {/* Create Collection Case Drawer */}
      <CreateCaseDrawer
        open={isCreateCaseModalOpen}
        onOpenChange={setIsCreateCaseModalOpen}
        onCaseCreated={handleCaseCreated}
        prePopulatedCustomer={{
          customerId: customer.id,
          customerName: customer.customerName,
          accountNumber: customer.accountNumber,
        }}
        selectedTransactions={selectedTransactions.map(invoice => convertInvoiceToTransaction(invoice))}
      />

      {/* Create Dispute Modal */}
      <CreateDisputeModal
        open={isCreateDisputeModalOpen}
        onOpenChange={setIsCreateDisputeModalOpen}
        selectedTransactions={selectedTransactionsAsTransaction}
        onSave={() => {
          setIsCreateDisputeModalOpen(false);
          setSelectedTransactionIds(new Set());
        }}
      />

      {/* Send Copy Invoice Modal */}
      <SendEmailModal
        open={isSendCopyInvoiceModalOpen}
        onOpenChange={setIsSendCopyInvoiceModalOpen}
        customerName={customer.customerName}
        customerEmail={customer.contact ?? ""}
        customerId={customer.id}
        isBulkEmail={selectedTransactionIds.size > 1}
        bulkCustomerCount={selectedTransactionIds.size}
        allowedCategoryTypes={["transactions", "general"]}
        allowedTemplateTypes={["email"]}
        onSuccess={() => {
          setIsSendCopyInvoiceModalOpen(false);
          setSelectedTransactionIds(new Set());
        }}
      />

      {/* Add Note Modal */}
      <AddNoteModal
        open={isAddNoteModalOpen}
        onOpenChange={setIsAddNoteModalOpen}
        selectedTransactions={selectedTransactions.map(invoice => convertInvoiceToTransaction(invoice))}
        onSave={handleSaveNote}
      />

      {/* Floating Action Panel - SelectionToolbar */}
      {selectedTransactionIds.size > 0 && (
        <SelectionToolbar
          selectedCount={selectedTransactionIds.size}
          totals={{
            count: selectedTransactionIds.size,
            totalAmount: selectedTotals.totalAmount,
            totalOutstanding: selectedTotals.totalOutstanding,
            currency: selectedTotals.currency,
          }}
          selectedTransactions={selectedTransactionsAsTransaction}
          baseCurrency={baseCurrency}
          onClearSelection={handleClearSelection}
          onExportCurrentView={handleExportSelected}
          onExportAllColumns={handleExportSelected}
          onSendReminders={() => toast.success("Sending reminders...")}
          onAllocateReceipt={() => toast.success("Opening allocate receipt...")}
          onSendCopyInvoice={handleSendCopyInvoice}
          onCreateCollectionCase={handleCreateCollectionCase}
          onCreatePromise={handleCreatePromise}
          onCreateDispute={handleCreateDispute}
          onAddNote={handleAddNote}
        />
      )}
    </div>
  );
}
